/**
 * What a platform super-admin can actually reach, and what this console cannot yet draw.
 *
 * The mechanism is the tenant administration console's, deliberately re-stated rather than imported
 * — see `docs/adr/0021-two-consoles-and-no-shared-shell.md` for why nothing is shared between the
 * channel applications yet. The three questions it answers are the same three:
 *
 *   - visible   — show it in the rail
 *   - reachable — allow it to be opened
 *   - readOnly  — open it, but do not offer the commands
 *
 * **What is different here is the proportion, and it is still the worst on the platform.**
 * `16 WP15 Platform Machinery.dc.html` draws sixteen pages and most of them have no operation a
 * browser can call. That is not a gap in this file: it is the measured state of the services behind
 * a platform control plane, re-derived by `tests/contract/console-screen-support.test.ts`, whose
 * per-screen table names the drawn element behind every absence. Every blocked page carries its
 * reason below, in that table's own words, so the reason survives being read.
 *
 * **No count of blocked pages is written here, and that is deliberate rather than coy.** The
 * previous version of this docblock wrote "fifteen" in two places and defended it by saying
 * `tests/page-support.test.ts` re-derives it — which is true of the derivation and was not true of
 * the prose: three pages became drawable in one change and both sentences went stale together. That
 * test names the pages, and naming is what a reader can act on. Run it.
 *
 * ## Why so many, in one sentence each
 *
 * Three causes, and the first two are the same cause twice:
 *
 *  1. **The service is internal.** Every operation behind the page is under `/internal/v1`, which
 *     the gateway does not expose —
 *     [ADR 0029](../../../docs/adr/0029-the-gateway-is-api-01s-data-plane-and-refuses-the-workload-surface.md)
 *     §2: EXP-001 refuses at load any route whose `pathPrefix` begins `/internal`. Most of them.
 *  2. **There is no listing, only a write or a by-identifier read.** The operation needs an
 *     identifier the page exists to discover. Usually found *inside* cause 1, which is why serving a
 *     public route alone would leave the board with nothing to ask for.
 *  3. **Nothing publishes the field the column draws.** The regions column, the index lag, the
 *     feature freshness state, the plan's own limits, the `Overridden by` column — and a route
 *     cannot fix any of them.
 *
 * And a fourth, which had never been one of the three and is now closed on three pages:
 *
 *  4. **A listing existed, was reachable, and answered one tenant's rows.** ADR 0048 made that
 *     disqualifying for a page titled `Platform …` and ADRs 0051, 0052 and 0053 answered it the way
 *     [ADR 0039](../../../docs/adr/0039-a-platform-scoped-audit-read-is-a-separate-operation-and-a-separate-scope.md)
 *     answered it for the audit log: a **separate** operation under a **separate** scope, with the
 *     tenant-bounded read left byte-identical. `gateway`, `partner` and `pflags` were the three, and
 *     two of them still carry absences that a tenancy decision could never have reached — see each.
 *
 * ## And the tenant is not free here, which is this console's own finding
 *
 * The tenant administration console is signed in to one tenant and sends `tenantId` on every
 * request, so a path parameter spelled `{tenantId}` costs it nothing. **That is not true of this
 * console**, whose first page exists to discover which tenants there are. So
 * `/v1/tenants/{tenantId}/…` is not a reachable address from a platform board even when the route is
 * public, and most of the sixteen pages are addressed exactly that way.
 *
 * Nothing here talks to a service. Scopes arrive in the access token, which is the only authority on
 * what an operator may do, and this module decides only what to draw. Every command still goes to a
 * service that checks again.
 */

export interface PageDefinition {
  readonly id: string;
  /** The page's own title, as the design's `PAGES` table writes it. */
  readonly title: string;
  /**
   * The rail's label, where the design's `NAV` uses a shorter one than the page title.
   *
   * Seven of the sixteen differ, which is more than any other console: the menu says `Tenants`,
   * `Event schemas`, `Dead letters`, `Search index`, `Workflows`, `Data catalogue` and `Models and
   * features` where the pages are titled at greater length. Both are the design's own words, so both
   * are kept rather than one being chosen.
   *
   * The eight that agree carry `railLabel` anyway, set to the title. That is deliberate: this field
   * is read from the design's `NAV` and the title from its `PAGES`, two tables that happen to agree
   * on those rows, and writing the agreement down is what lets `navigation.test.ts` compare each
   * list against its own table rather than inferring one from the other.
   *
   * `audit` is the row that proved it was worth doing. This file had its rail label as `Audit`,
   * which is the **tenant** administration console's label for its own audit page and is what gets
   * copied along with the module; the design's `NAV` says `Platform audit`. Nothing but a comparison
   * against `NAV` would have caught a plausible word in a rail nobody had rendered yet.
   */
  readonly railLabel?: string;
  /** The design's `NAV` group this item sits in, in that file's own words. */
  readonly section: string;
  /** Read requirements, OpenAPI's shape: any of these alternatives, all scopes within one. */
  readonly readRequirements: readonly (readonly string[])[];
  readonly writeRequirements?: readonly (readonly string[])[];
  /** Why the page exists, in the operator's terms — the design's own `PAGES` description. */
  readonly purpose: string;
  /** Why this page cannot be built yet, if it cannot. */
  readonly blockedBy?: string;
  /** The services this page reads from, by their canonical identifiers. */
  readonly owners: readonly string[];
  /** The Material Symbols Rounded glyph, read from `NAV` in the design file. */
  readonly glyph: string;
}

export type PageAccess = "full" | "read_only" | "forbidden";

export interface ResolvedPage {
  readonly page: PageDefinition;
  readonly access: PageAccess;
  readonly visible: boolean;
}

/**
 * Why most of the pages below carry an empty `readRequirements`, and four of them do not.
 *
 * **Because naming a scope here is what grants it.**
 * `tooling/scripts/lib/channel-scopes.mjs` derives each Keycloak client's grant from the string
 * literals in its own application's source, read from the TypeScript AST and intersected with the
 * published scope vocabulary. So a scope written in this file is a scope the whole channel client
 * holds, and a scope written only in a comment is not — comments are not AST nodes.
 *
 * The tenant administration console names a scope on each of its blocked pages, and that is
 * harmless there: three pages, and the scopes are a tenant's own. Repeating the habit here would
 * grant a platform super-admin client **a platform-control scope per blocked page, spendable on
 * nothing** — the gateway's publish, the replay approval, the kill switch, the risk list — because
 * every one of those pages is blocked and every one of those operations is internal. That is
 * precisely the direction `channel-scopes.mjs` says every hand-maintained grant list drifts in,
 * arriving by derivation instead of by hand.
 *
 * So the scope each blocked page *would* spend is recorded in that page's comment, where a reader
 * who serves its route will find it and where the derivation cannot. Recorded as **Q-176**, because
 * it is a convention this file is inventing rather than one the repository had already settled, and
 * because it has a cost: a page that becomes buildable needs its scope moved out of the comment, and
 * nothing fails if somebody forgets. `tests/page-support.test.ts` is where that bound lives — it
 * requires every page that is *not* blocked to name a scope sufficient to open a listing on one of
 * its owners.
 *
 * **The convention has now been exercised in the direction it was written for, three times at
 * once.** `gateway`, `partner` and `pflags` each carried their owners' scopes in a comment while
 * blocked; each page's own ADR served a listing, and each scope moved out of the comment into
 * `readRequirements` in the same change that drew the board. Q-176's residual risk — a page drawn
 * while still carrying a stale `blockedBy`, which would slip through both bounds — did not occur,
 * and `page-support.test.ts` would have failed either way, because it requires a blocked page to
 * have **no** listing behind it.
 *
 * **Four pages name a scope now, and all four cross the tenancy boundary.** `audit:platform:read`
 * was the first and is still the most privileged read this platform publishes: one token, every
 * tenant's audit log. ADRs 0051, 0052 and 0053 added three more of exactly that kind —
 * `gateway:platform:read`, `partner-credentials:platform:read` and `feature-management:platform:read`
 * — each a separate operation under a separate scope, each leaving its tenant-bounded sibling
 * byte-identical.
 *
 * **Q-139** records that nobody has decided who may hold a scope that reads every tenant: whether
 * holding it needs a second person or a time bound, or whether such a read should itself be audited.
 * Naming these here settles none of that. It is the question's second occasion rather than its
 * answer, and the three new scopes land in the same position `audit:platform:read` has been in since
 * ADR 0039 — granted to `tdelivr-platform-console` and to nothing else, by derivation, because this
 * console named them. Two of the three are more sensitive than the audit log in one specific way,
 * and that is why the ADRs argue their projections rather than their routes:
 * `partner-credentials:platform:read` reads every tenant's live partner keys as a mask, and
 * `feature-management:platform:read` reads every tenant's rollout configuration.
 *
 * Naming a scope here is what grants it. The realm change these four imply is the operator's to
 * apply; nothing in this repository applies one.
 */
export const PLATFORM_AUDIT_SCOPE = "audit:platform:read";
export const GATEWAY_ROUTES_SCOPE = "gateway:platform:read";
export const PARTNER_CREDENTIALS_SCOPE = "partner-credentials:platform:read";
export const PLATFORM_FLAGS_SCOPE = "feature-management:platform:read";
/**
 * ADR 0092's reads, and the one pair here that is not the cross-tenant half of anything.
 *
 * A plan is platform-wide — `Starter` is one row, not one per tenant — so there is no
 * tenant-bounded sibling for these to be the wider version of. `:platform:` is in the name as a
 * statement rather than a convention: a scope without it is one somebody will grant to a tenant
 * client, and `tenant:entitlement:read` must not open the catalogue of what every other tenant
 * could buy.
 *
 * The **write** is deliberately not a constant here. `channel-scopes.mjs` derives a client's grant
 * from the scope literals an application's own source names, so a `plan:platform:write` written
 * anywhere in this console would grant a platform super-admin client the right to define plans —
 * for a form that is drawn and refused. The same decision the tenant administration console took
 * for `booking-channel:write`, and the reason that refusal's sentence spells the scope in prose.
 */
export const PLANS_READ_SCOPE = "plan:platform:read";

/**
 * The four the event platform added, and why they are here rather than beside their boards.
 *
 * `channel-scopes.mjs` walks the whole of `apps/platform-console/src`, so a literal in a board's own
 * module grants the scope just as well. They are collected here anyway, because a reader asking
 * "what can this console reach across the tenancy boundary" looks in one file, and the answer being
 * eight constants in five files is how that question stops being answerable.
 *
 * `event-schema:platform:read` is the odd one of the eight: EVT-01's rows carry **no tenant column**
 * (`tenant_aware: false`), so it crosses no tenancy boundary. What it crosses is a projection
 * boundary — the schema document stays inside and the identity comes out — which ADR 0057 records.
 * It is named `:platform:` for consistency with its seven siblings rather than because it is
 * cross-tenant, and that is worth one sentence here so nobody reads the name as a tenancy claim.
 */
export const EVENT_SCHEMAS_SCOPE = "event-schema:platform:read";
export const EVENT_CATALOGUE_SCOPE = "event-catalog:platform:read";
export const DEAD_LETTERS_SCOPE = "dead-letter:platform:read";
export const EVENT_REPLAYS_SCOPE = "event-replay:platform:read";

/**
 * The tenant listing's scope, which is the one on this list that is not named `:platform:`.
 *
 * `tenant:read:any` was published long before this console existed, as the broad half of TEN-01's
 * by-identifier read — `tenant:read` for your own tenant, `tenant:read:any` for anybody's. The
 * listing takes the same scope rather than minting a `tenant:platform:read` beside it, because it
 * reads the same rows the same way and a second name for the same authority is a second thing to
 * grant and forget.
 *
 * It is the widest grant on this list by a distance: it reads every tenant on the platform. That is
 * what the page is.
 */
export const TENANTS_SCOPE = "tenant:read:any";

/**
 * The ten the last four cohorts added, taking this console from four pages of sixteen to thirteen.
 *
 * Collected here for the same reason as the four above: the question "what can this console reach
 * across the tenancy boundary" has to have one place that answers it. Eighteen constants in one
 * file is a long list; eighteen in eleven files is not a list at all.
 *
 * Six of the ten carry a note, because six are not the plain cross-tenant read their name suggests:
 *
 *   - **`search-indexer:platform:read`** is one scope over three listings — checkpoints, rebuild
 *     requests and index dead letters (ADR 0064 §2). The three are one operator question and were
 *     published together under one scope rather than three, which is the only decision in these ten
 *     that reduces the vocabulary rather than adding to it.
 *   - **`workflows:`, `schedules:` and `delayed-timers:platform:read`** are three services, and the
 *     board spends all three on one grid. ADR 0061 §7 refuses to compose them into one listing
 *     behind one scope: a composed read would make one service's availability decide whether another
 *     service's rows exist, and would put the merge in a service rather than in the client that
 *     draws it.
 *   - **`risk-cases:platform:read`** reads every tenant's open fraud investigations. ADR 0070
 *     withholds the subject of each one in the projection, not in the client — the scope buys the
 *     case and its state, never the person it is about. `fraud-rules:platform:read` is spent beside
 *     it and buys rule-set *versions*, not the rules: ADR 0071 §9 withholds the rule bodies and
 *     their digest, because a rule body is the evasion instructions for the rule.
 *   - **`slo:platform:read`** is the one of the ten that reads no tenant data at all. An objective
 *     is a statement about this platform's own behaviour, so like `event-schema:platform:read` it is
 *     named `:platform:` for consistency with its siblings and crosses no tenancy boundary. Worth
 *     the sentence so the name is not read as a tenancy claim.
 *
 * Q-139 still has no answer — nobody has decided who may hold a scope that reads every tenant, nor
 * whether such a read should itself be audited. These ten are its third occasion. The realm change
 * they imply is the operator's to apply; nothing in this repository applies one.
 */
export const SEARCH_INDEX_SCOPE = "search-indexer:platform:read";
export const WORKFLOWS_SCOPE = "workflows:platform:read";
export const SCHEDULES_SCOPE = "schedules:platform:read";
export const DELAYED_TIMERS_SCOPE = "delayed-timers:platform:read";
export const RISK_CASES_SCOPE = "risk-cases:platform:read";
export const FRAUD_RULES_SCOPE = "fraud-rules:platform:read";
export const DATA_CATALOGUE_SCOPE = "data-catalog:platform:read";
export const ML_FEATURES_SCOPE = "feature-serving:platform:read";
export const MODEL_DEPLOYMENTS_SCOPE = "model-serving:platform:read";
export const SLO_SCOPE = "slo:platform:read";

/**
 * The platform console's pages, in the design file's own order and groups.
 *
 * Each `blockedBy` is the per-screen entry from `tests/contract/console-screen-support.test.ts`,
 * condensed to the element that decided the verdict. That file re-derives every one of them from
 * `docs/design/api-operations.json` on every run, so a route that lands or a field that gets
 * published fails it and forces this file to be re-read — which is the opposite of the hazard R-17
 * recorded, where a page stayed recorded as unsupported for several commits after its listing was
 * served.
 */
export const platformPages: readonly PageDefinition[] = [
  {
    id: "tenants",
    glyph: "domain",
    railLabel: "Tenants",
    owners: ["TEN-01", "TEN-06"],
    title: "Tenant list and provisioning",
    section: "Tenants",
    purpose:
      "Every business on the platform. Provisioning is a saga; a failed step compensates instead of leaving a half-made tenant.",
    /*
     * **Built.** Its first absence was the one that made this console's whole shape:
     * `POST /v1/tenants` existed and there was no `GET`, so the page that exists to discover which
     * tenants there are could not list them — which is also why `tenantId` is not a free parameter
     * anywhere else in this file. TEN-01 publishes `GET /v1/tenants` now, over an immutable
     * `(created_at, tenant_id)` ordering, under `tenant:read:any` and nothing else: a listing of
     * tenants has no tenant to be scoped to, so there is no narrower grant that could answer a safe
     * subset.
     *
     * **Three of the four absences remain and the page is degraded, not whole.** The regions column
     * has no field anywhere — `tenant.regions`, `tenant.countries` and `tenant.regionCodes` were
     * swept across every contract — so it draws the design's own em dash. The saga progress read
     * needs a provisioning identifier, which is not a tenant identifier and which nothing on this
     * page can discover, so `provisioning · step 4 of 6` draws as `provisioning`. And
     * `POST /v1/provisioning` publishes **no request body at all**, so the designed six-field form
     * is drawn and refused. Each is argued in `src/tenants.ts`.
     *
     * `tenant-provisioning:write` is deliberately not asked for. The form it would spend on cannot
     * be filled, so holding it would be a permission to provision with no screen that provisions.
     */
    readRequirements: [[TENANTS_SCOPE]],
  },
  {
    id: "gateway",
    glyph: "alt_route",
    owners: ["EXP-001", "API-01"],
    title: "Gateway routes",
    section: "Machinery",
    purpose:
      "Publishing a route changes authorisation for the whole platform. Every version needs a requester and a different approver.",
    /*
     * **Drawn, and still the page with the largest hole on this console.**
     *
     * The rows were reachable before anything changed here — `EXP-001 GET /v1/gateway/routes`
     * publishes `routes.pathPrefix`, `environment`, `routes.requiredScopes` and `routes.version`,
     * which is the design's own row field for field — and the one thing wrong with it was that its
     * own description says it is "filtered to the tenant on the caller's verified token", while a
     * platform route table spans the platform. ADR 0048 is the rule that made that disqualifying and
     * [ADR 0051](../../../docs/adr/0051-a-platform-scoped-gateway-route-listing-is-a-separate-operation-and-a-separate-scope.md)
     * is the answer: `GET /v1/platform/gateway/routes` under `gateway:platform:read`, with the
     * tenant-bounded read untouched and a **narrower** projection, because publishing one tenant's
     * upstream wiring and rate limits to another operator is not what the board draws.
     *
     * **What a platform-scoped read did not and could not give this page is its purpose sentence.**
     * Nothing anywhere publishes who requested a route version or who approved it — `requestedBy`,
     * `approvedBy`, `requester` and `approver` swept across every published operation, none exists —
     * and API-01 holds both on a governed history it publishes under `/internal/v1` only. Three of
     * the design's four badges and three of its four tiles are the same absence, because this read
     * answers the routes one gateway **loaded** and a version awaiting approval was never loaded.
     * No column is added for any of it: `src/gateway-routes.ts` argues each absence and **Q-188**
     * carries them together.
     */
    readRequirements: [[GATEWAY_ROUTES_SCOPE]],
  },
  {
    id: "schemas",
    glyph: "schema",
    railLabel: "Event schemas",
    owners: ["EVT-01"],
    title: "Event schema registry",
    section: "Machinery",
    purpose:
      "Published contracts are immutable. A change is a new version; the old one stays readable forever.",
    /*
     * **Served.** This comment said "EVT-01 publishes no scope at all", and it was true twice over
     * when written: every operation was under `/internal/v1`, and the three scopes it did enforce
     * reached no contract because `requireScope` was called from inside a shared helper, which
     * `publish-route-scopes.mts` cannot read (ADR 0054).
     *
     * Both are closed. The helper defect was fixed across sixteen services, so `event-schema:read`
     * and `event-schema:publish` publish; and
     * [ADR 0057](../../../docs/adr/0057-a-platform-scoped-event-schema-listing-is-a-separate-operation-and-the-schema-document-stays-inside.md)
     * served `GET /v1/platform/event-schemas` for this board.
     *
     * The board draws all three of its grid columns from published fields, which no other board of
     * this console can say. What it cannot draw is recorded rather than improvised: the design's
     * third row is a **draft** version with a `breaking` verdict, and a registration either inserts
     * an immutable row or is refused 409 — there is no draft to list and no failed check stored. The
     * four KPI tiles are Q-178's, which every board here carries.
     */
    readRequirements: [[EVENT_SCHEMAS_SCOPE]],
  },
  {
    id: "dlq",
    glyph: "mark_email_unread",
    railLabel: "Dead letters",
    owners: ["EVT-02", "EVT-03"],
    title: "Event catalogue and dead letters",
    section: "Machinery",
    purpose:
      "Events that couldn't be delivered wait here with their reason, so none are silently lost.",
    /*
     * **Served, by two operations, and this is the only page of this console that reads two
     * services.** ADR 0058 served EVT-02's catalogue listing and ADR 0059 served EVT-03's dead
     * letters, so both halves of the design's page have a source.
     *
     * Two of the design's cells stay unanswerable and are drawn the way this console draws an
     * unanswerable cell — the design's own em dash with the reason on it. The `Consumer` column has
     * no source at all: **neither service models a consumer**, so it is a field on a command rather
     * than a route anybody can serve. And the attempt counter behind `retrying 2 of 5`, `Poison` and
     * `Redriven today` is not published, and retry *execution* is explicitly not EVT-03's
     * responsibility, so the counter may not even be its field to publish.
     *
     * Both scopes are named because the page spends both. A board reading two services needs two,
     * and `channel-scopes.mjs` grants exactly what is written here.
     */
    readRequirements: [[EVENT_CATALOGUE_SCOPE, DEAD_LETTERS_SCOPE]],
  },
  {
    id: "replay",
    glyph: "replay",
    railLabel: "Replay control",
    owners: ["EVT-04"],
    title: "Replay control",
    section: "Machinery",
    purpose:
      "Replays run with a concurrency limit and per-partition checkpoints. Sensitive streams need two approvers.",
    /*
     * **Served** by ADR 0060's `GET /v1/platform/event-replays`.
     *
     * The board draws `1 of 2 approvals` from two published counts rather than from any approver's
     * name, which is the point of publishing `requiredApprovals` at all: the one-or-two rule is a
     * server rule and a client recomputing it would restate it.
     *
     * Three of the design's cells are recorded as unanswerable rather than approximated. The
     * `38%` progress is **not computable** — a checkpoint carries a next offset and no start, so
     * there is no denominator — and the board draws the partition fraction the counts can say
     * instead, in the design's own `N of M` grammar. `48,210 events` is a plan **ceiling**, not a
     * count of events replayed, so it is not what that cell means. And `partitions 0–11` needs the
     * partition list, which travels with the broker offsets this read withholds.
     */
    readRequirements: [[EVENT_REPLAYS_SCOPE]],
  },
  {
    id: "search",
    glyph: "manage_search",
    railLabel: "Search index",
    owners: ["SEA-02"],
    title: "Search index control",
    section: "Machinery",
    purpose: "Projection checkpoints, dead-lettered events and rebuilds for every search index.",
    /*
     * Drawn by ADR 0064: three platform listings under one scope — checkpoints, rebuild requests,
     * index dead letters.
     *
     * **The `Lag` column was recorded here as un-drawable and that reading was wrong.** It said the
     * design writes `42 s`, a duration, while the platform publishes an offset, and that
     * `checkpoint.lagSeconds` is published by nothing. The first half is true and the conclusion did
     * not follow: the checkpoint also carries `indexedThrough`, an instant, and `now` minus an
     * instant is a duration. The subtraction is the board's to do (ADR 0064 §1), so the column needed
     * a route and not a field. Two of the design's three rows now come out word for word, both
     * offsets through its own grouping and both durations through that subtraction.
     *
     * What is still not drawn, and the one assumption:
     *
     *   - `rebuild 61%`. `rebuild_requests.status` is `requested | engine_reset` and nothing counts
     *     progress. Drawn as the word `rebuilding` with no figure.
     *   - **The line `lagging` is measured against is published nowhere.** No freshness objective,
     *     no per-alias target. The board reuses this console's own `STALE_AFTER_MS` as
     *     `CURRENT_WITHIN_MS`, which sits inside the bound the design's own two rows set, and every
     *     badge's `title` says the word is a rendering and the duration beside it is the
     *     measurement. A published per-index freshness objective is OBS-01's shape of thing rather
     *     than SEA-02's, and would replace the reuse.
     *   - A checkpoint row has no creation instant and no version, and a row is a roll-up of
     *     several, so the panel's `Created` and `Version` draw the design's own em dash.
     */
    readRequirements: [[SEARCH_INDEX_SCOPE]],
  },
  {
    id: "workflow",
    glyph: "account_tree",
    railLabel: "Workflows",
    owners: ["WRK-01", "WRK-02", "WRK-03"],
    title: "Workflow and schedules",
    section: "Machinery",
    purpose: "Orchestrated workflows, timers and scheduled jobs.",
    /*
     * Drawn by ADRs 0061–0063: three services, three scopes, one grid.
     *
     * All three scopes are spent on one page and ADR 0061 §7 refuses to compose them into one
     * listing behind one scope. The merge is the client's, in WRK-01, WRK-02, WRK-03 order, which is
     * the design's own source-chip order and its own row order. A composed read would let one
     * service's availability decide whether another service's rows exist.
     *
     * What is still not drawn:
     *
     *   - **The whole "needs a person" apparatus.** The `Stuck · 2 · past deadline` tile, the `human`
     *     row tint, the `waiting on a person` badge and `Open approval`. Nothing models a deadline on
     *     a workflow definition or a start command, so no row of this board can reach `human` — and
     *     `RowDetail` keys its action off the state class, so `Open approval` falls out rather than
     *     being special-cased away.
     *   - `nightly 23:59`. WRK-02 publishes `59 23 * * *` and **no prose vocabulary is published
     *     anywhere**, for `nightly` or for an arbitrary expression. The expression is drawn as
     *     published rather than rendered by a cron-to-prose writer in a browser, which would have to
     *     answer for `0,15,30,45 9-17 * * 1-5` as confidently as for the one expression the design
     *     happens to draw. Same call `event-catalogue.ts` makes writing a UUID where the design
     *     writes `evt_88K2`.
     *   - A delayed command's `Step`. WRK-03 holds a command type and a status and no step, and the
     *     command type is already that row's first column, so repeating it there would be a wrong
     *     answer rather than a missing one.
     */
    readRequirements: [[WORKFLOWS_SCOPE, SCHEDULES_SCOPE, DELAYED_TIMERS_SCOPE]],
  },
  {
    id: "risk",
    glyph: "shield",
    railLabel: "Risk and fraud",
    owners: ["RSK-01", "RSK-02", "RSK-05", "RSK-06"],
    title: "Risk and fraud",
    section: "Risk and data",
    purpose: "Signals, decisions, cases, rules and the blocklist.",
    /*
     * Drawn by ADRs 0070 and 0071, and **drawn degraded on purpose** — two of its three columns are
     * the design's own em dash, and that is the measured verdict rather than a shortfall to fix
     * later.
     *
     * The rows are RSK-05's cases. What the two withheld columns would need:
     *
     *   - **`Signal`.** ADR 0072 refuses RSK-01's listing: a signal is an assertion of suspicion
     *     about an identified person. The column would still be empty if it published — RSK-01 holds
     *     a classification, a severity and a producer name, and the design's cell writes a sentence
     *     (`5 cards in 10 min`) that no field holds. **Q-210.**
     *   - **`Decision`.** `block | review | allow` is RSK-03's vocabulary, and RSK-03 is not an owner
     *     of this page and publishes only an evaluate command with nothing reading a decision back.
     *     **Not filled from `disposition`**, which is a different enum
     *     (`confirmed_fraud | false_positive | inconclusive`) about a closed investigation; that is
     *     drawn in the panel under its own name. **Q-211.**
     *
     * RSK-02 is spent beside RSK-05 and draws no cell, so it feeds one sentence under the grid rather
     * than panel lines. A risk case carries no policy key, no rule identifier and no policy version,
     * and the one field that would point at a rule is withheld as a re-identifier — so there is no
     * published relation in either direction, and a `Fraud policy` panel line from "whichever rule
     * set shares the tenant" would have been invented. The sentence says a page in hand, never a
     * figure under a label meaning a total (ADR 0071 §9).
     *
     * The closing note "Blocklist entries show a reason and an expiry, never just a flag" is drawn
     * word for word and describes a record with no read of any kind (ADR 0073); the footnote says it
     * has no rows behind it.
     */
    readRequirements: [[RISK_CASES_SCOPE, FRAUD_RULES_SCOPE]],
  },
  {
    id: "data",
    glyph: "dataset",
    railLabel: "Data catalogue",
    owners: ["DAT-03"],
    title: "Data catalogue and lineage",
    section: "Risk and data",
    purpose:
      "What each dataset came from and what it feeds, in both directions. Publishing needs an approver.",
    /*
     * Drawn by ADR 0069. The lineage cells are the interesting part and the join is exact, not a
     * near-miss: a lineage array holds the same `datasetId` values the rows are keyed by,
     * `lineage_edges` is tenant-scoped so a row's lineage can never name another tenant's dataset
     * (ADR 0069 §4), and the map is keyed on the `(tenantId, datasetId)` pair anyway. A miss — the
     * referenced dataset is on a later page — falls back to the full published URN in Geist Mono with
     * the reason on the cell, rather than being shortened to its last segment into something that
     * resembles a name.
     *
     * What is not drawn:
     *
     *   - **All three designed badge words.** `published` is not one of DAT-03's five lifecycle
     *     states; `awaiting approval` is **unmodelled** — a `dataset_versions` row cannot exist
     *     without an `approved_by`, so a proposal in flight is not a row and would need a new
     *     aggregate rather than a route; `quality check failed` is DAT-02's, which this page does not
     *     own. The five words drawn are DAT-03's own.
     *   - `Lineage gaps · 0` needs a distinction nothing models: an empty edge array is both "no
     *     upstream yet" and "no upstream ever".
     *   - The panel's `Last changed` is the design's own em dash. `dataset_versions` is append-only
     *     by `REVOKE UPDATE, DELETE` — a publication adds a row with a new version — so there is no
     *     "last changed" instant to publish.
     *   - The approver's name. DAT-03 holds `requested_by`/`approved_by` and enforces their
     *     difference at three layers; ADR 0069 §5 withholds both. The purpose sentence "Publishing
     *     needs an approver" is drawn word for word and the names are not.
     *
     * `restricted` shares the `caution` class with `provisional`. The design files draw a tenth state
     * class, `lock`, that `StateClassKey` does not carry — **Q-193** — and a restricted dataset is
     * exactly what the tenth would be for. Distinct words and distinct titles meanwhile, so nothing
     * is shown by colour alone.
     */
    readRequirements: [[DATA_CATALOGUE_SCOPE]],
  },
  {
    id: "ml",
    glyph: "model_training",
    railLabel: "Models and features",
    owners: ["ML-01", "ML-02"],
    title: "Model serving and features",
    section: "Risk and data",
    purpose: "For every feature, freshness is the field that matters.",
    /*
     * Drawn by ADRs 0067 and 0068.
     *
     * **The reading recorded here was that freshness "is the field that does not exist", and that
     * was wrong in the same way the search board's `Lag` column was.** No contract publishes
     * `freshnessState`, and none needs to: a feature carries `newestExpiresAt` and
     * `maximumStalenessSeconds`, and the instant the value was written is the difference between
     * them. So the `Freshness` column is exact arithmetic over two published fields rather than a
     * missing enum, and `missing` is `newestExpiresAt === null` — the design's own word for it.
     *
     * What is still not drawn:
     *
     *   - **`Fallback`, a third of the grid.** The design draws four strategies; ML-02 holds one
     *     literal and `null`, and `last good value` is a behaviour that service does the opposite
     *     of. Drawn as the design's em dash rather than as the one literal dressed up as four.
     *   - `Shadow tests · 1` needs a traffic split or a shadow flag. Unmodelled.
     *
     * ML-01 is spent beside ML-02 and fills **no panel line**, unlike the dead-letters board's
     * secondary listing: nothing publishes a relation between a feature and a deployment, and the
     * only field that could name one — `inputFields` — is withheld as the model's feature vector and
     * its inference allowlist. ADR 0068 §7 says ML-01's listing "answers one tile and no column",
     * and the count it answers is said in a sentence under the grid, because no board of this console
     * draws the designed tile row (Q-178).
     */
    readRequirements: [[ML_FEATURES_SCOPE, MODEL_DEPLOYMENTS_SCOPE]],
  },
  {
    id: "slo",
    glyph: "monitoring",
    railLabel: "SLOs and telemetry",
    owners: ["OBS-01", "OBS-02"],
    title: "SLOs and telemetry",
    section: "Health",
    purpose: "Service objectives with the error budget left, not just uptime.",
    /*
     * Drawn by ADR 0066 — and **the word `telemetry` in this page's own title is answered by a
     * refusal rather than a route.**
     *
     * The chip names two owners and the board calls one. ADR 0065 records that OBS-01 publishes
     * nothing for this page and will not: its whole state is four in-process counters with no tenant
     * field, in a horizontally scaled gateway, so a published figure would change between two
     * refreshes without anything having happened — and the designed screen draws no telemetry
     * element to put one in. That is a decision, which is why this board has no second listing and
     * no degraded-secondary branch: there is nothing to degrade.
     *
     * One scope, and the page stays degraded with it served:
     *
     *   - **`burning fast`, one of three badge states, and the `Burning fast · 2` tile.** Both need a
     *     burn rate. `evaluateBudget` computes none, `burn` appears nowhere in that service's source,
     *     and ADR 0066 §4 withholds the window bounds *with* the event counts, so a client cannot
     *     derive one either. The board draws a two-state badge, omits the third, and says so under
     *     the grid.
     *   - `Alerts open · 3` is further still: `service.yaml` puts alert delivery under
     *     `non_responsibilities`, so no service in this cohort holds an alert at all.
     *   - `under 800 ms`, half of the `Objective` cell. The threshold is inside a withheld SLI URN
     *     and is not prose anywhere.
     *
     * The budget crosses as a ratio and its terms do not, by decision: the six event counts over an
     * objective's window are one operator's request volume, which on this platform is its order
     * volume, and a window plus a consumed count reconstructs it. The design draws `12%`, and a
     * percentage is publishable without its denominator.
     */
    readRequirements: [[SLO_SCOPE]],
  },
  {
    id: "partner",
    glyph: "hub",
    railLabel: "Partner API",
    owners: ["API-02", "API-03"],
    title: "Partner API",
    section: "Health",
    purpose: "Partner credentials and webhook delivery.",
    /*
     * **Drawn with two of its three columns, and the third is API-03's to publish.**
     *
     * Two findings this file recorded before anything was served have both held up, and both were
     * found by running `tests/page-support.test.ts` rather than by reading the cohort table:
     *
     *  1. `console-screen-support.test.ts` records this board's absence as
     *     "no GET /v1/tenants/{tenantId}/partners", and a listing existed at another address —
     *     `API-02 GET /v1/partner-credentials`, served by ADR 0044. True of that address and
     *     misleading about that service. R-59.
     *  2. That listing publishes `credentials.clientIdSuffix`, which **is** the design's
     *     `key ···9F2A`, so the `absent` need recorded against that element — over
     *     `credential.maskedSecret`, `credential.lastFour` and `credential.secretSuffix` — is
     *     answered under a fourth name nobody swept for. Q-181.
     *
     * What blocked the page was neither: that listing is "scoped to the authenticated tenant", and
     * this is a board of every partner on the platform.
     * [ADR 0052](../../../docs/adr/0052-a-platform-scoped-partner-credential-listing-is-a-separate-operation-and-a-separate-scope.md)
     * serves `GET /v1/platform/partner-credentials` under `partner-credentials:platform:read`, with
     * the tenant-bounded read untouched and the same five fields withheld in the same SQL — the
     * argon2id digest most of all, because a page of every tenant's digests is an offline guessing
     * target for every live partner key on the platform.
     *
     * **A platform-scoped read was not enough.** The `Webhook` column is API-03's delivery outcome,
     * and API-03 publishes nothing a browser can call: three operations, all `/internal/v1`. The
     * column is drawn with the design's own `—` and the reason on it rather than removed or
     * invented, and whether API-03 publishes a delivery summary is API-03's decision. **Q-195.**
     */
    readRequirements: [[PARTNER_CREDENTIALS_SCOPE]],
  },
  {
    id: "audit",
    glyph: "history",
    // `Platform audit` in the rail as well as on the page, which is what this file's `NAV` says.
    // It read `Audit` — the tenant administration console's label, copied with the module — until
    // `navigation.test.ts` compared it against the design.
    railLabel: "Platform audit",
    owners: ["AUD-01"],
    title: "Platform audit",
    section: "Audit",
    purpose: "Who did what, across every tenant. Append-only by trigger.",
    /*
     * **The one page of sixteen that is drawn, and the one that no route unblocked.**
     *
     * `AUD-01 GET /v1/platform/audit-records` publishes every field the board draws — `occurredAt`,
     * `actorChain.subject`, `outcome`, `tenantId` and ten more — and, unlike the tenant-bounded
     * `GET /v1/audit-records` beside it, it crosses the tenancy boundary on purpose.
     * [ADR 0039](../../../docs/adr/0039-a-platform-scoped-audit-read-is-a-separate-operation-and-a-separate-scope.md)
     * served it under one new scope, `audit:platform:read`, and left the tenant-bounded read exactly
     * as it was.
     *
     * It is `degraded` rather than finished, and the hole is the export form's `From` and `To`: the
     * platform listing publishes `limit` and `before` only, because ADR 0039 decided the tenancy
     * boundary and nothing else. Q-140 carries the date range. The board draws the control and
     * refuses it with that reason on it, rather than dropping the form.
     *
     * **`audit:platform:read` is named by `platform-audit.ts` and by the board**, and naming it is
     * what grants it: `tooling/scripts/lib/channel-scopes.mjs` derives each Keycloak client's grant
     * from string literals in its own application's source. Q-139 records that nobody has decided
     * who may hold the most privileged read on the platform, whether it needs a second person or a
     * time bound, or whether reading every tenant's audit log should itself be audited. That
     * question is **not** settled by this page existing, and the realm change it implies is the
     * operator's to apply.
     */
    readRequirements: [[PLATFORM_AUDIT_SCOPE]],
    // No write. The design's own closing note is "There is no edit or delete anywhere on this page,
    // because the store does not allow it", and AUD-01 is append-only by database trigger. The
    // `Export platform audit log` form is a read, not a command, which is why it is not a write
    // requirement — and why it is drawn disabled rather than offered.
  },
  {
    id: "settings",
    glyph: "settings",
    railLabel: "Platform settings",
    owners: ["CFG-01"],
    title: "Platform settings",
    section: "Settings",
    purpose:
      "Defaults every tenant inherits unless they override them. Releases are versioned and need two people.",
    // **CFG-01 publishes no scope at all**: every operation is `/internal/v1`.
    //
    // **Re-measured 17 October, and the old reason made this look smaller than it is.** It read
    // as a routing problem — internal operations that need a public sibling — and it is a
    // modelling one. `configuration_releases` is keyed by `(tenant_id, service_id,
    // environment, release_number)` with `tenant_id NOT NULL`, and there is no reserved
    // platform tenant anywhere in the service. So **a platform default that tenants inherit is
    // a concept CFG-01 does not have**: every release belongs to one tenant, nothing is
    // inherited, and "overridden by 2 tenants" is a comparison between a thing that exists and
    // a thing that does not.
    //
    // That makes this comparable to `finance · Settlement` rather than smaller than it, which
    // is the opposite of what was recorded when the four remaining screens were ranked.
    readRequirements: [],
    blockedBy:
      "CFG-01 models no platform-level configuration: every configuration_releases row carries a tenant_id and no reserved platform tenant exists, so the defaults this page lists are not a thing the service has, and neither is the inheritance that would make a tenant's row an override of one. No GET /v1/platform/configuration can be published until that is decided, and the 'Overridden by' count comes after it rather than before.",
  },
  {
    id: "pflags",
    glyph: "toggle_on",
    railLabel: "Platform flags",
    owners: ["CFG-02"],
    title: "Platform flags",
    section: "Settings",
    purpose: "Capabilities switched on per tenant or platform-wide.",
    /*
     * **Drawn, and the one of the three that needed only the tenancy change.**
     *
     * The same correction as `partner` above, found the same way. ADR 0045 served
     * `CFG-02 GET /v1/features` — a tenant's flags as a summary without their allocation seed — so
     * "no GET /v1/tenants/{tenantId}/features" was true of that address and not of the service. It
     * publishes `flagKey`, `killSwitch` and `rolloutBasisPoints`, which is this board's `Flag` and
     * `State` columns; the one thing it could not answer is the `Scope` column, because the design's
     * cell is `ten_7Q2M, ten_3KD9` and the listing was scoped to the authenticated tenant. R-59.
     *
     * [ADR 0053](../../../docs/adr/0053-a-platform-scoped-flag-summary-listing-is-a-separate-operation-and-a-separate-scope.md)
     * serves `GET /v1/platform/features` under `feature-management:platform:read`, answering the
     * same `FeatureSummary` projection across every tenant with the tenant-bounded read untouched —
     * `allocationSeed` withheld on both, and across tenants that withholding is the whole decision:
     * it is the value a rollout's bucket is hashed with, so every tenant's seeds make every tenant's
     * rollouts computable before they arrive.
     *
     * Every column this board draws now has a published source. The absences it still carries are
     * the ones `tenant-admin · Feature flags` carries too and were already recorded: the expiry
     * (Q-151), the four stat tiles (R-04, Q-178) and a two-person write the design's four-field form
     * cannot satisfy. Two are new and belong to this board rather than that one — the `Scope`
     * column's `platform` value, which CFG-02 models nothing for (**Q-190**), and the `State`
     * column's fraction, whose denominator is the tenants that hold the flag because nothing
     * publishes how many tenants exist (**Q-189**).
     */
    readRequirements: [[PLATFORM_FLAGS_SCOPE]],
  },
  {
    id: "plans",
    glyph: "workspace_premium",
    railLabel: "Plans and entitlements",
    /*
     * **TEN-03 alone now, where this entry named TEN-01 beside it.** TEN-01 was there for the
     * `Tenants` column, and that column is drawn as an absence: ADR 0083 published a listing of
     * tenants, which answers how many exist and not how many are on each plan. So this page reads
     * nothing from TEN-01, and `page-support.test.ts` holds every owner here to being a service
     * the page actually reads from.
     */
    owners: ["TEN-03"],
    title: "Plans and entitlements",
    section: "Settings",
    purpose: "The plans tenants can buy, and each tenant's usage against them.",
    /*
     * **Served, and its recorded reason made three claims of which two still hold.** The entry
     * that stood here read:
     *
     * > Nothing on the platform models a plan: no name, no limits, no price. TEN-03 holds
     * > entitlements one capability at a time, which is a different thing. The `Tenants · 3`
     * > column needs GET /v1/tenants, which does not exist, and the entitlement write needs both a
     * > tenantId and a capability this page would discover.
     *
     *   - **The first was true**, and ADR 0092 records the plan — in TEN-03, because a plan's
     *     limits are written in the same vocabulary as a tenant's entitlements, and a service of
     *     its own would be the 143rd for one record with one screen.
     *   - **The second is closed as written and did not help.** ADR 0083 published
     *     `GET /v1/tenants`, which answers how many tenants exist and not how many are on each
     *     plan. Nothing records which plan a tenant is on, so that column is still an absence —
     *     and it is the sharpest near miss in this cohort, because "the route it named now exists"
     *     is exactly what makes a stale blocker look closed.
     *   - **The third still holds**, and the entitlement write stays off this board.
     *
     * The scope is `plan:platform:read`, with `platform` in the name deliberately: a plan has no
     * tenant, so a scope without it is one somebody will grant to a tenant client.
     */
    readRequirements: [["plan:platform:read"]],
  },
];

/** The page the rail offers first, which is the design's own `NAV` order. */
export const platformPageIds: readonly string[] = platformPages.map((page) => page.id);

/**
 * Whether a set of scopes satisfies one OpenAPI-shaped requirement list.
 *
 * Any alternative, all scopes within it. An empty requirement list means "no read scope is published
 * for this", which is not the same as "anyone may read it" — `resolvePage` treats it as unsatisfiable
 * rather than as satisfied, because a page whose read nothing can authorize is a page nobody should
 * be told they can open.
 */
function satisfied(
  held: ReadonlySet<string>,
  requirements: readonly (readonly string[])[],
): boolean {
  return requirements.some(
    (alternative) => alternative.length > 0 && alternative.every((required) => held.has(required)),
  );
}

/**
 * What one page is to one operator: full, read-only, or out of reach.
 *
 * A page the platform cannot serve is `forbidden` and **visible**, which is the opposite of the
 * dispatch console's rule and deliberate. A platform operator who cannot see fifteen of sixteen rail
 * items would conclude the console is broken; one who can see them and is told what each is waiting
 * on can read the readiness page and know where the work is. The rail says `· not available` on
 * those, which is the design's own `view only` affordance put to the honest use.
 */
export function resolvePage(page: PageDefinition, held: ReadonlySet<string>): ResolvedPage {
  if (page.blockedBy !== undefined) {
    return { page, access: "forbidden", visible: true };
  }
  if (!satisfied(held, page.readRequirements)) {
    return { page, access: "forbidden", visible: false };
  }
  const writes = page.writeRequirements;
  const access: PageAccess = writes === undefined || satisfied(held, writes) ? "full" : "read_only";
  return { page, access, visible: true };
}

export interface NavigationSection {
  readonly section: string;
  readonly pages: readonly ResolvedPage[];
}

/**
 * The rail, in the design's own groups and order.
 *
 * A group with nothing visible in it is dropped rather than drawn empty, which is what the design
 * does when its `NAV` group has no items — it never has none, so this is this console's rule for a
 * case the reference does not reach.
 */
export function buildNavigation(held: ReadonlySet<string>): readonly NavigationSection[] {
  const sections: { section: string; pages: ResolvedPage[] }[] = [];
  for (const page of platformPages) {
    const resolved = resolvePage(page, held);
    if (!resolved.visible) continue;
    const existing = sections.find((candidate) => candidate.section === page.section);
    if (existing === undefined) sections.push({ section: page.section, pages: [resolved] });
    else existing.pages.push(resolved);
  }
  return sections;
}

/** Whether a page may be opened at all, by its identifier. */
export function canOpen(pageId: string, held: ReadonlySet<string>): boolean {
  const page = platformPages.find((candidate) => candidate.id === pageId);
  if (page === undefined) return false;
  return resolvePage(page, held).access !== "forbidden";
}

/**
 * The pages this operator's role is short of, excluding the ones the platform cannot serve.
 *
 * The exclusion is the same decision the tenant administration console made and matters more here:
 * an operator told their role is short of fifteen pages would ask for permissions that would change
 * nothing. The platform-blocked pages are reported on the readiness page instead, with the reason.
 */
export function pagesNotInRole(
  held: ReadonlySet<string>,
): readonly { readonly id: string; readonly title: string; readonly needs: readonly string[] }[] {
  return platformPages
    .filter((page) => page.blockedBy === undefined)
    .filter((page) => !satisfied(held, page.readRequirements))
    .map((page) => ({
      id: page.id,
      title: page.title,
      needs: page.readRequirements[0] ?? [],
    }));
}

/** The pages no operation can draw, with the measured reason each is waiting on. */
export function blockedPages(): readonly PageDefinition[] {
  return platformPages.filter((page) => page.blockedBy !== undefined);
}
